<template>
  <div class="matrix-support-form">
    <!-- Destino: informação fixa, não é campo -->
    <p class="ms-destination">
      <span class="ms-destination-label">Para:</span>
      {{ matrixTeam.name }}
    </p>

    <!-- Modalidade: dois cartões selecionáveis -->
    <fieldset class="ms-block">
      <legend class="ms-label">
        Modalidade
      </legend>
      <div class="ms-cards">
        <label
          v-for="option in modalityOptions"
          :key="option._id"
          class="ms-card"
          :class="{ 'is-selected': modelValue.modality === option._id }"
        >
          <input
            type="radio"
            name="matrixSupportModality"
            class="ms-visually-hidden"
            :value="option._id"
            :checked="modelValue.modality === option._id"
            @change="update('modality', option._id)"
          >
          <span class="ms-card-title">
            <span
              class="ms-card-dot"
              aria-hidden="true"
            />
            {{ option.name }}
          </span>
          <span class="ms-card-desc">{{ option.description }}</span>
        </label>
      </div>
    </fieldset>

    <!-- Motivo: pílulas selecionáveis -->
    <fieldset class="ms-block">
      <legend class="ms-label">
        Motivo
      </legend>
      <div class="ms-pills">
        <label
          v-for="option in reasonOptions"
          :key="option._id"
          class="ms-pill"
          :class="{ 'is-selected': modelValue.reason === option._id }"
        >
          <input
            type="radio"
            name="matrixSupportReason"
            class="ms-visually-hidden"
            :value="option._id"
            :checked="modelValue.reason === option._id"
            @change="update('reason', option._id)"
          >
          {{ option.name }}
        </label>
      </div>
      <p
        v-if="showErrors && !modelValue.reason"
        class="ms-error"
      >
        Campo obrigatório.
      </p>
    </fieldset>

    <!-- Pergunta clínica -->
    <div class="ms-block">
      <lila-textarea
        :model-value="modelValue.question"
        label="Pergunta clínica"
        placeholder="Descreva o que a equipe precisa da equipe matricial."
        :validation="{ $error: showErrors && !modelValue.question.trim(), required: true }"
        @update:model-value="update('question', $event)"
      />
      <p class="ms-hint">
        Ex.: dor refratária apesar de morfina 10 mg de 4 em 4 horas; família em dúvida sobre as diretivas.
      </p>
    </div>
  </div>
</template>

<script>
import Textarea from "@components/shared/fields/Textarea.vue";
import {
  MATRIX_TEAM,
  MODALITY_OPTIONS,
  REASON_OPTIONS
} from "@/services/palliative/matrixSupportApi";

export default {
  name: "LilaMatrixSupportRequest",
  components: {
    "lila-textarea": Textarea
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    showErrors: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue"],
  data() {
    return {
      matrixTeam: MATRIX_TEAM,
      modalityOptions: MODALITY_OPTIONS,
      reasonOptions: REASON_OPTIONS
    };
  },
  methods: {
    update(field, value) {
      this.$emit("update:modelValue", { ...this.modelValue, [field]: value });
    }
  }
};
</script>

<style lang="scss">
.matrix-support-form {
  --ms-primary: #4f3f82;
  --ms-primary-soft: #ece9f5;
  --ms-ink: #2b2b3a;
  --ms-label: #484848;
  --ms-muted: #6b6b7b;
  --ms-border: #dcd8e6;
  --ms-error: #cc0f35;
}

/* Ritmo vertical: 24px entre todos os blocos */
.matrix-support-form .ms-block {
  margin: 0 0 24px;
  padding: 0;
  border: 0;
  min-width: 0;
}

.matrix-support-form .ms-label,
.matrix-support-form .textarea-component .label {
  display: block;
  margin: 0 0 10px 20px;
  padding: 0;
  font-size: 16px;
  line-height: 18px;
  font-weight: 700;
  color: var(--ms-label);
}

.matrix-support-form .textarea-component.field {
  margin-bottom: 0;
}

/* Destino */
.matrix-support-form .ms-destination {
  margin: 0 0 24px;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  color: var(--ms-ink);
  background-color: var(--ms-primary-soft);
}

.matrix-support-form .ms-destination-label {
  margin-right: 4px;
  font-weight: 700;
  color: var(--ms-primary);
}

/* Acessível para leitor de tela e teclado, invisível na tela */
.matrix-support-form .ms-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

/* Modalidade: cartões */
.matrix-support-form .ms-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.matrix-support-form .ms-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  padding: 14px 16px;
  border: 2px solid transparent;
  border-radius: 12px;
  background-color: #ffffff;
  cursor: pointer;
  transition: border-color 0.15s ease-in-out;

  &:hover {
    border-color: var(--ms-border);
  }

  &.is-selected {
    border-color: var(--ms-primary);
  }

  &:focus-within {
    outline: 2px solid var(--ms-primary);
    outline-offset: 2px;
  }
}

.matrix-support-form .ms-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--ms-ink);
}

.matrix-support-form .ms-card-dot {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border: 2px solid var(--ms-border);
  border-radius: 50%;
  background-color: #ffffff;
}

.matrix-support-form .ms-card.is-selected .ms-card-dot {
  border-color: var(--ms-primary);
  box-shadow: inset 0 0 0 3px #ffffff;
  background-color: var(--ms-primary);
}

.matrix-support-form .ms-card-desc {
  padding-left: 24px;
  font-size: 13px;
  line-height: 17px;
  color: var(--ms-muted);
}

/* Motivo: pílulas */
.matrix-support-form .ms-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.matrix-support-form .ms-pill {
  position: relative;
  padding: 8px 16px;
  border: 1px solid var(--ms-border);
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  color: var(--ms-primary);
  background-color: #ffffff;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out;

  &:hover {
    background-color: var(--ms-primary-soft);
  }

  &.is-selected {
    border-color: var(--ms-primary);
    color: #ffffff;
    background-color: var(--ms-primary);
  }

  &:focus-within {
    outline: 2px solid var(--ms-primary);
    outline-offset: 2px;
  }
}

/* Dica e erro */
.matrix-support-form .ms-hint {
  margin: 8px 0 0 20px;
  font-size: 12px;
  line-height: 16px;
  color: var(--ms-muted);
}

.matrix-support-form .ms-error {
  margin: 8px 0 0 20px;
  font-size: 12px;
  color: var(--ms-error);
}

@media (max-width: 600px) {
  .matrix-support-form .ms-cards {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
